import React, { useState } from "react";
import Header from "../_components/_global/WelcomeHeader";

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'article'
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    "All",
    "Travel Tips",
    "City Guides",
    "Culture",
    "Food",
    "Budget Travel",
  ];

  const blogPosts = [
    {
      id: 1,
      category: "City Guides",
      title: "Montreal: Where European Charm Meets North American Innovation",
      excerpt:
        "Discover the unique blend of French heritage and modern creativity in Canada's cultural capital. From cobblestone streets to cutting-edge festivals.",
      author: "Sarah Chen",
      date: "March 15, 2024",
      readTime: "8 min read",
      image:
        "https://www.bonjourquebec.com/sites/default/files/styles/image_process_webp/public/2020-05/Jour-6-Cite-memoire-vieux%20Montreal-2.jpg.webp?itok=VqGFIsdy",
      featured: true,
    },
    {
      id: 2,
      category: "City Guides",
      title: "The Ultimate Guide to Barcelona's Architecture",
      excerpt:
        "Explore Gaudí's masterpieces and modernist wonders scattered throughout this vibrant Catalan capital.",
      author: "Michael Torres",
      date: "March 12, 2024",
      readTime: "6 min read",
      image:
        "https://d3dqioy2sca31t.cloudfront.net/Projects/cms/production/000/020/484/original/d0531471711b367b94abfd4dbc29e6ae/spain-barcelona-sagrada-familia-080416-az.jpg",
    },
    {
      id: 3,
      category: "Budget Travel",
      title: "Budget-Friendly European City Breaks",
      excerpt:
        "Discover how to explore Europe's most beautiful cities without breaking the bank. Tips from experienced travelers.",
      author: "Emma Wilson",
      date: "March 10, 2024",
      readTime: "7 min read",
      image:
        "https://static.independent.co.uk/2025/04/25/13/42/iStock-1498516775.jpg",
    },
    {
      id: 4,
      category: "Food",
      title: "Street Food Adventures in Bangkok",
      excerpt:
        "A culinary journey through Bangkok's vibrant street food scene. What to eat and where to find the best vendors.",
      author: "David Kim",
      date: "March 8, 2024",
      readTime: "5 min read",
      image:
        "https://backpackersunited.in/_next/image?url=https%3A%2F%2Fbpu-images-v1.s3.eu-north-1.amazonaws.com%2Fuploads%2F1717407948643_Nightlife.png&w=3840&q=75",
    },
    {
      id: 5,
      category: "Travel Tips",
      title: "Solo Travel Safety Tips for First-Timers",
      excerpt:
        "Essential advice for your first solo adventure. Stay safe while exploring new destinations independently.",
      author: "Lisa Anderson",
      date: "March 5, 2024",
      readTime: "6 min read",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSaaNKvbzPo-wZmPOGpR7FtN0GR7KZ57qDrMg&s",
    },
    {
      id: 6,
      category: "Culture",
      title: "Cultural Etiquette Around the World",
      excerpt:
        "Navigate different cultures with confidence. Learn the do's and don'ts of international travel.",
      author: "James Park",
      date: "March 3, 2024",
      readTime: "8 min read",
      image:
        "https://connectpls.com/wp-content/uploads/Navigating_Cultural_Etiquette.jpeg",
    },
    {
      id: 7,
      category: "City Guides",
      title: "Weekend in Amsterdam: A Perfect Itinerary",
      excerpt:
        "Make the most of 48 hours in Amsterdam. From canal tours to museum hopping and everything in between.",
      author: "Sophie Martin",
      date: "March 1, 2024",
      readTime: "7 min read",
      image:
        "https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/KeizersgrachtReguliersgrachtAmsterdam.jpg/1200px-KeizersgrachtReguliersgrachtAmsterdam.jpg",
    },
  ];

  const montrealArticle = {
    title: "Montreal: Where European Charm Meets North American Innovation",
    author: "Sarah Chen",
    date: "March 15, 2024",
    readTime: "8 min read",
    category: "City Guides",
    content: [
      {
        type: "intro",
        text: "Montreal is a city of delightful contradictions, where 17th-century cobblestone streets lead to cutting-edge art galleries, and where you can hear French, English, and dozens of other languages mixing in sidewalk cafés. This cultural metropolis offers visitors an intoxicating blend of European sophistication and North American dynamism that you won't find anywhere else on the continent.",
      },
      {
        type: "paragraph",
        text: "As Canada's second-largest city, Montreal has evolved from a fur trading post to become one of North America's most culturally rich destinations. The city's unique position as a French-speaking island in an English-speaking continent has created a distinctive identity that celebrates both heritage and innovation.",
      },
      {
        type: "section",
        title: "1. Old Montreal (Vieux-Montréal)",
        text: "Step back in time as you wander through Old Montreal's narrow cobblestone streets. This historic district, dating back to 1642, feels more like a European quarter than a North American neighborhood. The stunning Notre-Dame Basilica, with its dramatic Gothic Revival architecture and spectacular light show, anchors this area. Don't miss the Old Port, now transformed into a recreational and cultural space with festivals, skating in winter, and beach activities in summer.",
      },
      {
        type: "quote",
        text: '"Montreal is the perfect city for those who want Europe without crossing the Atlantic. It\'s Paris meets Brooklyn with a Canadian twist." — Travel journalist',
      },
      {
        type: "section",
        title: "2. Mount Royal Park",
        text: "Designed by Frederick Law Olmsted (who also created New York's Central Park), Mount Royal Park is the green heart of Montreal. The lookout points offer breathtaking views of the city skyline and the St. Lawrence River. In autumn, the park explodes with colors as maple leaves turn brilliant shades of red and gold. Winter transforms it into an urban winter wonderland perfect for snowshoeing and cross-country skiing.",
      },
      {
        type: "section",
        title: "3. The Underground City (RÉSO)",
        text: "Montreal's extensive underground network, known as RÉSO, spans over 33 kilometers of tunnels connecting shopping centers, hotels, apartments, universities, and metro stations. This \"indoor city\" is particularly appreciated during Montreal's harsh winters, allowing residents and visitors to navigate the city center without stepping outside. It's one of the largest underground complexes in the world.",
      },
      {
        type: "section",
        title: "4. Jean-Talon Market",
        text: "One of the largest public markets in North America, Jean-Talon Market is a feast for all senses. Local farmers, fishmongers, butchers, and bakers offer the freshest Quebec produce. In summer and fall, the market overflows with local strawberries, corn, apples, and the region's famous maple syrup. The market also reflects Montreal's multiculturalism with vendors selling ingredients from around the world.",
      },
      {
        type: "section",
        title: "5. The Festival City",
        text: "Montreal earns its reputation as a festival city with over 100 festivals annually. The Montreal International Jazz Festival is the world's largest, while Just for Laughs is the biggest comedy festival globally. Winter doesn't slow things down with events like Igloofest, an electronic music festival where attendees dance outside in sub-zero temperatures.",
      },
    ],
    tableOfContents: [
      "Introduction",
      "Old Montreal",
      "Mount Royal Park",
      "The Underground City",
      "Jean-Talon Market",
      "The Festival City",
      "Conclusion",
    ],
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setViewMode("article");
    window.scrollTo(0, 0);
  };

  const handleBackToBlog = () => {
    setViewMode("list");
    setSelectedArticle(null);
  };

  const filteredPosts =
    selectedCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif;
          background: #ffffff;
          color: #1a1a1a;
        }
        
        .blog-container {
          min-height: 100vh;
          background: #ffffff;
          padding-top: 80px;
        }
        
        /* Blog List View */
        .blog-hero {
          text-align: center;
          padding: 60px 20px;
        }
        
        .blog-title {
          font-size: 56px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 20px;
          line-height: 1.2;
        }
        
        .blog-subtitle {
          font-size: 20px;
          color: #6b7280;
          max-width: 700px;
          margin: 0 auto;
        }
        
        /* Featured Post */
        .featured-section {
          max-width: 1200px;
          margin: 0 auto 60px;
          padding: 0 20px;
        }
        
        .featured-label {
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 16px;
        }
        
        .featured-post {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
          cursor: pointer;
        }
        
        .featured-image {
          width: 100%;
          height: 400px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          position: relative;
          overflow: hidden;
        }
        
        .featured-content {
          padding: 20px 0;
        }
        
        .featured-category {
          color: #10b981;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 12px;
        }
        
        .featured-title {
          font-size: 32px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 16px;
          line-height: 1.3;
        }
        
        .featured-excerpt {
          font-size: 18px;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        
        .featured-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          color: #9ca3af;
          font-size: 14px;
        }
        
        /* Categories */
        .categories-section {
          background: #f9fafb;
          padding: 24px 0;
          margin-bottom: 48px;
          position: sticky;
          top: 80px;
          z-index: 10;
        }
        
        .categories-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          align-items: center;
          gap: 24px;
        }
        
        .category-btn {
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 20px;
          font-size: 14px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        
        .category-btn:hover {
          background: #f3f4f6;
        }
        
        .category-btn.active {
          background: #0f1720;
          color: white;
          border-color: #0f1720;
        }
        
        .search-box {
          margin-left: auto;
          position: relative;
        }
        
        .search-input {
          padding: 8px 16px 8px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          font-size: 14px;
          width: 250px;
          background: white;
        }
        
        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }
        
        /* Blog Grid */
        .blog-grid {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px 60px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
        }
        
        .blog-card {
          cursor: pointer;
          transition: transform 0.2s;
        }
        
        .blog-card:hover {
          transform: translateY(-4px);
        }
        
        .blog-card-image {
          width: 100%;
          height: 240px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          margin-bottom: 20px;
        }
        
        .blog-card-category {
          color: #6b7280;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 8px;
        }
        
        .blog-card-title {
          font-size: 20px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 12px;
          line-height: 1.4;
        }
        
        .blog-card-excerpt {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 16px;
        }
        
        .blog-card-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          color: #9ca3af;
        }
        
        .meta-separator {
          width: 4px;
          height: 4px;
          background: #d1d5db;
          border-radius: 50%;
        }
        
        /* Newsletter Section */
        .newsletter-section {
          background: #f9fafb;
          padding: 80px 20px;
          text-align: center;
        }
        
        .newsletter-title {
          font-size: 32px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 12px;
        }
        
        .newsletter-subtitle {
          font-size: 18px;
          color: #6b7280;
          margin-bottom: 32px;
        }
        
        .newsletter-form {
          max-width: 500px;
          margin: 0 auto;
          display: flex;
          gap: 12px;
        }
        
        .newsletter-input {
          flex: 1;
          padding: 12px 20px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 15px;
          background: white;
        }
        
        .newsletter-btn {
          padding: 12px 32px;
          background: #0f1720;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .newsletter-btn:hover {
          background: #1f2937;
        }
        
        .newsletter-note {
          font-size: 13px;
          color: #9ca3af;
          margin-top: 12px;
        }
        
        /* Article View */
        .article-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
        }
        
        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          text-decoration: none;
          font-size: 15px;
          margin-bottom: 32px;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: #4b5563;
        }
        
        .article-header {
          margin-bottom: 32px;
          padding-bottom: 32px;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .article-category {
          color: #10b981;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 16px;
        }
        
        .article-title {
          font-size: 48px;
          font-weight: 700;
          color: #0f1720;
          line-height: 1.2;
          margin-bottom: 24px;
        }
        
        .article-meta {
          display: flex;
          align-items: center;
          gap: 24px;
          color: #6b7280;
          font-size: 15px;
        }
        
        .author-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .share-btn {
          margin-left: auto;
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .share-btn:hover {
          background: #f9fafb;
        }
        
        .article-image {
          width: 100%;
          height: 450px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          margin-bottom: 48px;
        }
        
        .article-content {
          font-size: 17px;
          line-height: 1.8;
          color: #374151;
        }
        
        .article-content p {
          margin-bottom: 24px;
        }
        
        .article-section {
          margin-bottom: 32px;
        }
        
        .section-title {
          font-size: 28px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        
        .article-quote {
          padding: 24px;
          margin: 32px 0;
          background: #f9fafb;
          border-left: 4px solid #10b981;
          font-style: italic;
          color: #4b5563;
          font-size: 18px;
        }
        
        /* Table of Contents */
        .toc-sidebar {
          position: fixed;
          right: 40px;
          top: 200px;
          width: 200px;
        }
        
        .toc-title {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #9ca3af;
          margin-bottom: 16px;
        }
        
        .toc-list {
          list-style: none;
        }
        
        .toc-item {
          padding: 8px 0;
          font-size: 14px;
          color: #6b7280;
          cursor: pointer;
          transition: color 0.2s;
        }
        
        .toc-item:hover {
          color: #374151;
        }
        
        /* Related Articles */
        .related-section {
          margin-top: 80px;
          padding-top: 48px;
          border-top: 1px solid #e5e7eb;
        }
        
        .related-title {
          font-size: 28px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 32px;
        }
        
        .related-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        
        .related-card {
          cursor: pointer;
        }
        
        .related-image {
          width: 100%;
          height: 160px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 8px;
          margin-bottom: 12px;
        }
        
        .related-category {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        
        .related-title-text {
          font-size: 16px;
          font-weight: 600;
          color: #0f1720;
          line-height: 1.4;
        }
        
        /* CTA Section */
        .article-cta {
          background: #f9fafb;
          padding: 48px;
          border-radius: 12px;
          text-align: center;
          margin-top: 48px;
        }
        
        .cta-title {
          font-size: 24px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 12px;
        }
        
        .cta-text {
          font-size: 16px;
          color: #6b7280;
          margin-bottom: 24px;
        }
        
        .cta-form {
          max-width: 400px;
          margin: 0 auto;
          display: flex;
          gap: 12px;
        }
        
        .cta-input {
          flex: 1;
          padding: 10px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
        }
        
        .cta-btn {
          padding: 10px 24px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }
        
        /* Responsive */
        @media (max-width: 1200px) {
          .toc-sidebar {
            display: none;
          }
        }
        
        @media (max-width: 768px) {
          .blog-title {
            font-size: 36px;
          }
          
          .featured-post {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .blog-grid {
            grid-template-columns: 1fr;
          }
          
          .categories-wrapper {
            overflow-x: auto;
            gap: 12px;
          }
          
          .search-box {
            display: none;
          }
          
          .article-title {
            font-size: 32px;
          }
          
          .related-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <Header />

      <div className="blog-container">
        {viewMode === "list" ? (
          <>
            {/* Blog List View */}
            <div className="blog-hero">
              <h1 className="blog-title">Travel stories and guides</h1>
              <p className="blog-subtitle">
                Expert tips, destination guides, and travel inspiration from our
                community of explorers
              </p>
            </div>

            {/* Featured Post */}
            <div className="featured-section">
              <div className="featured-label">Featured</div>
              <div
                className="featured-post"
                onClick={() => handleArticleClick(blogPosts[0])}
              >
                <div
                  className="featured-image"
                  style={{
                    backgroundImage:
                      "url(https://www.bonjourquebec.com/sites/default/files/styles/image_process_webp/public/2020-05/Jour-6-Cite-memoire-vieux%20Montreal-2.jpg.webp?itok=VqGFIsdy)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                ></div>
                <div className="featured-content">
                  <div className="featured-category">City Guides</div>
                  <h2 className="featured-title">
                    Montreal: Where European Charm Meets North American
                    Innovation
                  </h2>
                  <p className="featured-excerpt">
                    Discover the unique blend of French heritage and modern
                    creativity in Canada's cultural capital. From cobblestone
                    streets in Old Montreal to cutting-edge festivals, this
                    bilingual metropolis offers an intoxicating mix of old-world
                    charm and contemporary culture.
                  </p>
                  <div className="featured-meta">
                    <span>Sarah Chen</span>
                    <span className="meta-separator"></span>
                    <span>March 15, 2024</span>
                    <span className="meta-separator"></span>
                    <span>8 min read</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="categories-section">
              <div className="categories-wrapper">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`category-btn ${
                      selectedCategory === category ? "active" : ""
                    }`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search articles"
                  />
                </div>
              </div>
            </div>

            {/* Blog Grid */}
            <div className="blog-grid">
              {filteredPosts.slice(1).map((post) => (
                <div
                  key={post.id}
                  className="blog-card"
                  onClick={() => handleArticleClick(post)}
                >
                  <div
                    className="blog-card-image"
                    style={{
                      backgroundImage: `url(${post.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  ></div>

                  <div className="blog-card-category">{post.category}</div>
                  <h3 className="blog-card-title">{post.title}</h3>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-meta">
                    <span>{post.author}</span>
                    <span className="meta-separator"></span>
                    <span>{post.date}</span>
                    <span className="meta-separator"></span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Newsletter */}
            <div className="newsletter-section">
              <h2 className="newsletter-title">
                Get travel tips in your inbox
              </h2>
              <p className="newsletter-subtitle">
                Join 50,000+ travelers receiving weekly inspiration and advice
              </p>
              <div className="newsletter-form">
                <input
                  type="email"
                  className="newsletter-input"
                  placeholder="Enter your email"
                />
                <button className="newsletter-btn">Subscribe</button>
              </div>
              <p className="newsletter-note">No spam. Unsubscribe anytime.</p>
            </div>
          </>
        ) : (
          <>
            {/* Article View */}
            <div className="article-container">
              <a
                href="#"
                className="back-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackToBlog();
                }}
              >
                ← Back to Blog
              </a>

              <div className="article-header">
                <div className="article-category">City Guides</div>
                <h1 className="article-title">{montrealArticle.title}</h1>
                <div className="article-meta">
                  <div className="author-info">
                    <span>👤</span>
                    <span>{montrealArticle.author}</span>
                  </div>
                  <span>📅 {montrealArticle.date}</span>
                  <span>⏱ {montrealArticle.readTime}</span>
                  <button className="share-btn">Share</button>
                </div>
              </div>

              <div
                className="article-image"
                style={{
                  backgroundImage:
                    "url(https://www.bonjourquebec.com/sites/default/files/styles/image_process_webp/public/2020-05/Jour-6-Cite-memoire-vieux%20Montreal-2.jpg.webp?itok=VqGFIsdy)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              ></div>

              <div className="article-content">
                {montrealArticle.content.map((section, index) => {
                  if (
                    section.type === "intro" ||
                    section.type === "paragraph"
                  ) {
                    return <p key={index}>{section.text}</p>;
                  } else if (section.type === "section") {
                    return (
                      <div key={index} className="article-section">
                        <h2 className="section-title">{section.title}</h2>
                        <p>{section.text}</p>
                      </div>
                    );
                  } else if (section.type === "quote") {
                    return (
                      <div key={index} className="article-quote">
                        {section.text}
                      </div>
                    );
                  }
                  return null;
                })}

                <div className="article-section">
                  <h2 className="section-title">Conclusion</h2>
                  <p>
                    Montreal stands as a testament to the beauty of cultural
                    fusion. It's a city that respects its past while embracing
                    the future, where tradition and innovation dance together on
                    every street corner. Whether you're drawn to its European
                    ambiance, its thriving arts scene, or its culinary
                    excellence, Montreal offers an urban experience unlike any
                    other in North America.
                  </p>
                  <p>
                    Plan your visit during summer to experience the festival
                    season in full swing, or brave the winter to discover why
                    Montrealers have turned cold weather into an art form.
                    Whatever season you choose, Montreal will charm you with its
                    joie de vivre and leave you planning your return before
                    you've even left.
                  </p>
                </div>
              </div>

              {/* Table of Contents Sidebar */}
              <div className="toc-sidebar">
                <div className="toc-title">Table of Contents</div>
                <ul className="toc-list">
                  {montrealArticle.tableOfContents.map((item, index) => (
                    <li key={index} className="toc-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Articles */}
              <div className="related-section">
                <h3 className="related-title">Related articles</h3>
                <div className="related-grid">
                  <div className="related-card">
                    <div
                      className="related-image"
                      style={{
                        backgroundImage:
                          "url(https://d3dqioy2sca31t.cloudfront.net/Projects/cms/production/000/020/484/original/d0531471711b367b94abfd4dbc29e6ae/spain-barcelona-sagrada-familia-080416-az.jpg)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    ></div>
                    <div className="related-category">City Guides</div>
                    <div className="related-title-text">
                      The Ultimate Guide to Barcelona's Architecture
                    </div>
                  </div>
                  <div className="related-card">
                    <div
                      className="related-image"
                      style={{
                        backgroundImage:
                          "url(https://backpackersunited.in/_next/image?url=https%3A%2F%2Fbpu-images-v1.s3.eu-north-1.amazonaws.com%2Fuploads%2F1717407948643_Nightlife.png&w=3840&q=75)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    ></div>
                    <div className="related-category">Food</div>
                    <div className="related-title-text">
                      Street Food Adventures in Bangkok
                    </div>
                  </div>
                  <div className="related-card">
                    <div
                      className="related-image"
                      style={{
                        backgroundImage:
                          "url(https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/KeizersgrachtReguliersgrachtAmsterdam.jpg/1200px-KeizersgrachtReguliersgrachtAmsterdam.jpg)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    ></div>
                    <div className="related-category">City Guides</div>
                    <div className="related-title-text">
                      Weekend in Amsterdam: A Perfect Itinerary
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="article-cta">
                <h3 className="cta-title">Enjoyed this article?</h3>
                <p className="cta-text">
                  Subscribe to get more travel guides delivered to your inbox
                </p>
                <div className="cta-form">
                  <input
                    type="email"
                    className="cta-input"
                    placeholder="Your email"
                  />
                  <button className="cta-btn">Subscribe</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default BlogPage;
